import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { effectiveRate, formatRate, isBanded } from '../../utils/bandRate';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Typography,
  TextField,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

interface Option {
  id: string;
  name: string;
}

interface ProductOption extends Option {
  activeIngredientDetails?: string;
}

// startDate/endDate are DateOnly "YYYY-MM-DD" strings.
interface TimePeriodOption extends Option {
  startDate: string;
  endDate: string;
}

// A file's own dates are midnight UTC (ExcelParserHelpers.GetDate), so the
// UTC calendar date is the date the file stated.
const toDateInput = (iso?: string): string => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
const formatDateInput = (date: string): string => {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString();
};

// A Farm/Field option — areaHectares is its boundary area, which becomes the
// Record's MappedAreaHectares (see Models/Record.cs).
interface FeatureOption extends Option {
  areaHectares: number | null;
}

interface ImportedRowSummary {
  id: string;
  businessName?: string;
  farmName?: string;
  fieldName?: string;
  seasonName?: string;
  productName?: string;
  productCategoryName?: string;
  geneStack?: string;
  rate?: number;
  unitName?: string;
  areaHectares?: number;
  bandPercent?: number;
  eventDate?: string;
}

// One paddock value the row will store on its field's season — see
// api/DTOs/FieldSeasonDto.cs (FieldSeasonValuePreviewDto).
interface FieldSeasonValue {
  label: string;
  rawValue: string;
  value?: string;
  matched: boolean;
}

interface Suggestions {
  alreadyMatched: boolean;
  existingRecordId?: string;
  existingFieldSeasonId?: string;
  // A field-season format row (CCA Supplementary Data): approving fills the
  // field's season and creates no Record — no Unit, Operation, date or
  // pests, and the Product is the (optional) variety.
  fieldSeasonOnly: boolean;
  fieldSeasonValues: FieldSeasonValue[];
  business?: Option;
  farm?: Option;
  field?: Option;
  timePeriod?: Option;
  product?: Option;
  productCategory?: Option;
  unit?: Option;
  operationType?: Option;
  region?: Option;
  geneStack?: Option;
  pests: { rawText: string; species?: Option }[];
  suggestedTimePeriodStart?: string;
  suggestedTimePeriodEnd?: string;
  similarUnmatchedCount: number;
  similarUnmatchedProductCount: number;
  similarUnmatchedCategoryCount: number;
  similarUnmatchedGeneStackCount: number;
  similarUnmatchedBusinessCount: number;
  similarUnmatchedFarmCount: number;
  similarUnmatchedFieldCount: number;
}

type Mode = 'existing' | 'new' | 'skip' | 'group';

interface FieldState {
  mode: Mode;
  existingId: string | null;
  newName: string;
}

const emptyField = (): FieldState => ({ mode: 'existing', existingId: null, newName: '' });

interface Props {
  row: ImportedRowSummary;
  open: boolean;
  onClose: () => void;
  onMatched: () => void;
}

// Same "one step per resolvable thing" shape as ImportMatchWizard.tsx (the
// CCA t_report_data wizard), adapted for /api/import-uploads/rows/{id}/...
// instead of /api/import-report-data/{reportId}/... — see
// ImportedRowMatchingService for why this is a separate implementation
// rather than a generalized shared one. A field-season row skips the
// record-only steps.
type StepKey = 'business' | 'farm' | 'field' | 'season' | 'product' | 'pests' | 'unit' | 'review';
const recordSteps: StepKey[] = ['business', 'farm', 'field', 'season', 'product', 'pests', 'unit', 'review'];
const fieldSeasonSteps: StepKey[] = ['business', 'farm', 'field', 'season', 'product', 'review'];
const stepLabels = (fieldSeasonOnly: boolean): Record<StepKey, string> => ({
  business: 'Business',
  farm: 'Farm',
  field: 'Field',
  season: fieldSeasonOnly ? 'Season' : 'Season & Date',
  product: fieldSeasonOnly ? 'Variety' : 'Product & Category',
  pests: 'Pests',
  unit: 'Rate Unit & Operation',
  review: 'Review',
});

// What the row's paddock columns will be stored as on the field's season.
const FieldSeasonValuesTable: React.FC<{ values: FieldSeasonValue[] }> = ({ values }) => (
  <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1 } }}>
    <TableHead>
      <TableRow>
        <TableCell>Column</TableCell>
        <TableCell>In file</TableCell>
        <TableCell>Stored as</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {values.map((v) => (
        <TableRow key={v.label}>
          <TableCell>{v.label}</TableCell>
          <TableCell>{v.rawValue}</TableCell>
          <TableCell>
            {v.matched ? (
              <strong>{v.value}</strong>
            ) : (
              <Chip size="small" color="warning" variant="outlined" label="Not in its list — kept as text" />
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const ImportedRowMatchWizard: React.FC<Props> = ({ row, open, onClose, onMatched }) => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);

  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [farms, setFarms] = useState<FeatureOption[]>([]);
  const [fields, setFields] = useState<FeatureOption[]>([]);
  const [timePeriods, setTimePeriods] = useState<TimePeriodOption[]>([]);
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productCategories, setProductCategories] = useState<Option[]>([]);
  const [units, setUnits] = useState<Option[]>([]);
  const [operationTypes, setOperationTypes] = useState<Option[]>([]);
  const [regions, setRegions] = useState<Option[]>([]);
  const [geneStacks, setGeneStacks] = useState<Option[]>([]);
  const [species, setSpecies] = useState<Option[]>([]);

  const [business, setBusiness] = useState<FieldState>(emptyField());
  const [businessParentId, setBusinessParentId] = useState<string | null>(null);
  const [farm, setFarm] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [field, setFieldState] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [timePeriod, setTimePeriod] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [timePeriodStart, setTimePeriodStart] = useState('');
  const [timePeriodEnd, setTimePeriodEnd] = useState('');
  // "YYYY-MM-DD", pre-filled from the file's own date; blank means fall
  // back to the Product's Default Use Date in the season, else 1 Jan in the
  // season, else (no season) the upload date.
  const fileDate = toDateInput(row.eventDate);
  const [recordDate, setRecordDate] = useState(fileDate);
  const [product, setProduct] = useState<FieldState>(emptyField());
  const [productSearch, setProductSearch] = useState('');
  const [productGroups, setProductGroups] = useState<Option[]>([]);
  const [productGroupSearch, setProductGroupSearch] = useState('');
  const [category, setCategory] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [geneStack, setGeneStack] = useState<FieldState>({ ...emptyField(), mode: 'skip' });
  const [pestFields, setPestFields] = useState<Record<string, FieldState>>({});
  const [unit, setUnit] = useState<FieldState>(emptyField());
  const [operationTypeId, setOperationTypeId] = useState<string | null>(null);
  const [regionId, setRegionId] = useState<string | null>(null);
  const [applyToSimilar, setApplyToSimilar] = useState(true);
  // Pre-fill (not auto-commit) this row's Product/Category/Gene Stack match
  // as the default for every OTHER still-unmatched row in the batch sharing
  // the same raw text for that one field — see ApplyProductToSimilar etc on
  // ResolveMatchRequest. Independent of applyToSimilar above, which requires
  // the whole row (business/farm/field/product) to match, not just one field.
  const [applyProductToSimilar, setApplyProductToSimilar] = useState(true);
  const [applyCategoryToSimilar, setApplyCategoryToSimilar] = useState(true);
  const [applyGeneStackToSimilar, setApplyGeneStackToSimilar] = useState(true);
  const [applyBusinessToSimilar, setApplyBusinessToSimilar] = useState(true);
  const [applyFarmToSimilar, setApplyFarmToSimilar] = useState(true);
  const [applyFieldToSimilar, setApplyFieldToSimilar] = useState(true);

  const productSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productGroupSearchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fieldSeasonOnly = !!suggestions?.fieldSeasonOnly;
  const steps = fieldSeasonOnly ? fieldSeasonSteps : recordSteps;
  const stepKey = steps[activeStep];
  const isSeedRow = fieldSeasonOnly || !!(row.productCategoryName?.toLowerCase().includes('seed') || row.geneStack);

  useEffect(() => {
    if (!open) return;
    setActiveStep(0);
    setError(null);
    setSuggestions(null);
    setLoading(true);
    setRecordDate(toDateInput(row.eventDate));
    // Clear any leftover product search results from the previous row —
    // otherwise, until this row's own search resolves, the duplicate-check
    // Alert (and the "Use Existing" dropdown) can briefly show an unrelated
    // product list left over from whatever the last row searched for.
    setProductSearch('');
    setProducts([]);
    setProductGroupSearch('');
    setProductGroups([]);

    (async () => {
      try {
        const [suggestionsRes, businessesRes] = await Promise.all([
          fetch(`/api/import-uploads/rows/${row.id}/match-suggestions`, { headers: authHeaders() }),
          fetch('/api/businesses/list', { headers: authHeaders() }),
        ]);
        if (!suggestionsRes.ok) throw new Error('Failed to load match suggestions');
        const s: Suggestions = await suggestionsRes.json();
        setSuggestions(s);
        if (businessesRes.ok) {
          const list = await businessesRes.json();
          setBusinesses(list.map((b: any) => ({ id: b.id, name: b.name })));
        }

        if (s.alreadyMatched) {
          setLoading(false);
          return;
        }

        setBusiness(
          s.business
            ? { mode: 'existing', existingId: s.business.id, newName: row.businessName || '' }
            : { mode: 'new', existingId: null, newName: row.businessName || '' }
        );
        // Suggestion wins even with no raw text at all — same reasoning as
        // TimePeriod below: an Auto Fill pre-set (see
        // ImportedRowMatchingService.AutoFillBlankFieldAsync) can resolve
        // Farm/Field with nothing in the row's own raw column, and gating on
        // raw text first would silently discard that and show "skip"
        // instead, forcing the admin to re-pick what was already resolved.
        // A field-season row can't skip Farm/Field/Season — its field's
        // season is keyed on them — so a blank one starts as "create new".
        const noneMode = s.fieldSeasonOnly ? 'new' : 'skip';
        setFarm(
          s.farm
            ? { mode: 'existing', existingId: s.farm.id, newName: row.farmName || '' }
            : row.farmName
              ? { mode: 'new', existingId: null, newName: row.farmName }
              : { mode: noneMode, existingId: null, newName: '' }
        );
        setFieldState(
          s.field
            ? { mode: 'existing', existingId: s.field.id, newName: row.fieldName || '' }
            : row.fieldName
              ? { mode: 'new', existingId: null, newName: row.fieldName }
              : { mode: noneMode, existingId: null, newName: '' }
        );
        setTimePeriod(
          // s.timePeriod alone (not row.seasonName) decides "existing" —
          // the row's own raw text can be blank (CCA Summary/CCA Market
          // Audit) while still carrying a suggestion pre-resolved from the
          // batch's default Season (see ImportBatch.DefaultTimePeriodId).
          s.timePeriod
            ? { mode: 'existing', existingId: s.timePeriod.id, newName: s.timePeriod.name }
            : row.seasonName
              ? { mode: 'new', existingId: null, newName: row.seasonName }
              : { mode: noneMode, existingId: null, newName: '' }
        );
        setTimePeriodStart(s.suggestedTimePeriodStart || '');
        setTimePeriodEnd(s.suggestedTimePeriodEnd || '');
        // The variety is optional on a field-season row.
        setProduct(
          s.product
            ? { mode: 'existing', existingId: s.product.id, newName: row.productName || '' }
            : s.fieldSeasonOnly && !row.productName
              ? { mode: 'skip', existingId: null, newName: '' }
              : { mode: 'new', existingId: null, newName: row.productName || '' }
        );
        // Seed the duplicate-check search too, so a boundary-less "Create
        // New" default (no alias/fuzzy match found) still surfaces a
        // similar-existing-product warning immediately, before the admin
        // touches the field — see the Product step's Create New branch.
        if (!s.product && row.productName) {
          setProductSearch(row.productName.split('(')[0].trim());
        }
        setCategory(
          s.productCategory
            ? { mode: 'existing', existingId: s.productCategory.id, newName: row.productCategoryName || '' }
            : row.productCategoryName
              ? { mode: 'new', existingId: null, newName: row.productCategoryName }
              : { mode: 'skip', existingId: null, newName: '' }
        );
        setGeneStack(
          s.geneStack
            ? { mode: 'existing', existingId: s.geneStack.id, newName: row.geneStack || '' }
            : row.geneStack
              ? { mode: 'new', existingId: null, newName: row.geneStack }
              : { mode: 'skip', existingId: null, newName: '' }
        );
        setUnit(
          s.unit
            ? { mode: 'existing', existingId: s.unit.id, newName: row.unitName || '' }
            : { mode: 'new', existingId: null, newName: row.unitName || '' }
        );
        setOperationTypeId(s.operationType?.id ?? null);
        setRegionId(s.region?.id ?? null);
        setPestFields(
          Object.fromEntries(
            s.pests.map((p) => [
              p.rawText,
              p.species
                ? { mode: 'existing' as const, existingId: p.species.id, newName: p.rawText }
                : { mode: 'new' as const, existingId: null, newName: p.rawText },
            ])
          )
        );
        setApplyToSimilar(s.similarUnmatchedCount > 0);
        setApplyProductToSimilar(s.similarUnmatchedProductCount > 0);
        setApplyCategoryToSimilar(s.similarUnmatchedCategoryCount > 0);
        setApplyGeneStackToSimilar(s.similarUnmatchedGeneStackCount > 0);
        setApplyBusinessToSimilar(s.similarUnmatchedBusinessCount > 0);
        setApplyFarmToSimilar(s.similarUnmatchedFarmCount > 0);
        setApplyFieldToSimilar(s.similarUnmatchedFieldCount > 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row.id]);

  const resolvedBusinessId = business.mode === 'existing' ? business.existingId : null;
  useEffect(() => {
    if (!resolvedBusinessId) {
      setFarms([]);
      return;
    }
    (async () => {
      const [layersRes, featuresRes] = await Promise.all([
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-layers`, { headers: authHeaders() }),
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-features`, { headers: authHeaders() }),
      ]);
      if (!layersRes.ok || !featuresRes.ok) return;
      const layers = await layersRes.json();
      const features = await featuresRes.json();
      const farmLayerIds = new Set(layers.filter((l: any) => l.category === 'Farm').map((l: any) => l.id));
      setFarms(features.filter((f: any) => farmLayerIds.has(f.spatialLayerId)).map((f: any) => ({ id: f.id, name: f.name, areaHectares: f.areaHectares ?? null })));
    })();
  }, [resolvedBusinessId, authHeaders]);

  const resolvedFarmId = farm.mode === 'existing' ? farm.existingId : null;
  // A picked field that isn't under the farm now chosen (the farm was
  // changed after the field was suggested) would otherwise stay selected,
  // unseen, and be saved — a Boonaldoon field on a Boonal row. It falls
  // back to creating the row's own field text under the new farm.
  const dropFieldOutside = useCallback((fieldIds: Set<string>) => {
    setFieldState((prev) =>
      prev.mode === 'existing' && prev.existingId && !fieldIds.has(prev.existingId)
        ? row.fieldName
          ? { mode: 'new', existingId: null, newName: row.fieldName }
          : { ...prev, existingId: null }
        : prev
    );
  }, [row.fieldName]);

  useEffect(() => {
    if (!resolvedBusinessId || !resolvedFarmId) {
      setFields([]);
      dropFieldOutside(new Set());
      return;
    }
    (async () => {
      const [layersRes, featuresRes] = await Promise.all([
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-layers`, { headers: authHeaders() }),
        fetch(`/api/businesses/${resolvedBusinessId}/spatial-features`, { headers: authHeaders() }),
      ]);
      if (!layersRes.ok || !featuresRes.ok) return;
      const layers = await layersRes.json();
      const features = await featuresRes.json();
      const fieldLayerIds = new Set(
        layers.filter((l: any) => l.category === 'Field' && l.parentSpatialFeatureId === resolvedFarmId).map((l: any) => l.id)
      );
      const farmFields = features.filter((f: any) => fieldLayerIds.has(f.spatialLayerId));
      setFields(farmFields.map((f: any) => ({ id: f.id, name: f.name, areaHectares: f.areaHectares ?? null })));
      dropFieldOutside(new Set(farmFields.map((f: any) => f.id)));
    })();
  }, [resolvedBusinessId, resolvedFarmId, authHeaders, dropFieldOutside]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [tpRes, catRes, unitRes, opRes, regionRes, geneStackRes, speciesRes] = await Promise.all([
        fetch('/api/time-periods', { headers: authHeaders() }),
        fetch('/api/product-categories', { headers: authHeaders() }),
        fetch('/api/units', { headers: authHeaders() }),
        fetch('/api/operation-types', { headers: authHeaders() }),
        fetch('/api/regions', { headers: authHeaders() }),
        fetch('/api/gene-stacks', { headers: authHeaders() }),
        fetch('/api/species', { headers: authHeaders() }),
      ]);
      if (tpRes.ok) setTimePeriods((await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name, startDate: t.startDate, endDate: t.endDate })));
      if (catRes.ok) setProductCategories((await catRes.json()).map((c: any) => ({ id: c.id, name: c.name })));
      if (unitRes.ok) setUnits((await unitRes.json()).map((u: any) => ({ id: u.id, name: u.name })));
      if (opRes.ok) setOperationTypes((await opRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
      if (regionRes.ok) setRegions((await regionRes.json()).map((r: any) => ({ id: r.id, name: r.name })));
      if (geneStackRes.ok) setGeneStacks((await geneStackRes.json()).map((g: any) => ({ id: g.id, name: g.name })));
      if (speciesRes.ok) setSpecies((await speciesRes.json()).map((s: any) => ({ id: s.id, name: s.name })));
    })();
  }, [open, authHeaders]);

  useEffect(() => {
    if (productSearchDebounce.current) clearTimeout(productSearchDebounce.current);
    // The wizard steps between rows (Next Unmatched, bulk-apply, etc.)
    // without unmounting, so a slow fetch from a PREVIOUS row's search term
    // can resolve after this effect has already moved on to a new one — a
    // classic out-of-order-response race. `cancelled` makes a stale
    // response a no-op instead of silently overwriting the current row's
    // duplicate-check results with an unrelated (often unfiltered,
    // alphabetically-first) product list.
    let cancelled = false;
    productSearchDebounce.current = setTimeout(async () => {
      const params = new URLSearchParams({ pageSize: '25' });
      if (productSearch) params.set('search', productSearch);
      const res = await fetch(`/api/products?${params.toString()}`, { headers: authHeaders() });
      if (!cancelled && res.ok) {
        const data = await res.json();
        setProducts(
          data.items.map((p: any) => ({
            id: p.id,
            name: p.name,
            activeIngredientDetails: (p.activeIngredientDetails || []).join(', ') || undefined,
          }))
        );
      }
    }, 350);
    return () => {
      cancelled = true;
      if (productSearchDebounce.current) clearTimeout(productSearchDebounce.current);
    };
  }, [productSearch, authHeaders]);

  useEffect(() => {
    if (productGroupSearchDebounce.current) clearTimeout(productGroupSearchDebounce.current);
    let cancelled = false;
    productGroupSearchDebounce.current = setTimeout(async () => {
      const params = new URLSearchParams({ pageSize: '25' });
      if (productGroupSearch) params.set('search', productGroupSearch);
      const res = await fetch(`/api/product-groups?${params.toString()}`, { headers: authHeaders() });
      if (!cancelled && res.ok) {
        const data = await res.json();
        setProductGroups(data.items.map((g: any) => ({ id: g.id, name: g.name })));
      }
    }, 350);
    return () => {
      cancelled = true;
      if (productGroupSearchDebounce.current) clearTimeout(productGroupSearchDebounce.current);
    };
  }, [productGroupSearch, authHeaders]);

  const seasonRange = useMemo(() => {
    if (timePeriod.mode === 'existing') {
      const tp = timePeriods.find((t) => t.id === timePeriod.existingId);
      return tp ? { start: tp.startDate, end: tp.endDate } : null;
    }
    if (timePeriod.mode === 'new' && timePeriodStart && timePeriodEnd) {
      return { start: timePeriodStart, end: timePeriodEnd };
    }
    return null;
  }, [timePeriod, timePeriods, timePeriodStart, timePeriodEnd]);

  const fallbackDateText = 'the matched Product’s Default Use Date in the season if set, else 1 Jan in the season, else (no season) the upload date';
  const recordDateHelperText = fileDate
    ? recordDate === fileDate
      ? 'From the file'
      : recordDate
        ? `Replaces the file’s date (${formatDateInput(fileDate)})`
        : `Blank — the file’s date (${formatDateInput(fileDate)}) will be used`
    : recordDate
      ? 'The file has no date for this row'
      : `Not in the file — leave blank to use ${fallbackDateText}`;

  const needsFarm = farm.mode !== 'skip';
  const needsField = field.mode !== 'skip';

  // A field-season row can't skip Farm/Field/Season (see the initial state above).
  const canAdvance = useMemo(() => {
    switch (stepKey) {
      case 'business':
        return business.mode === 'existing' ? !!business.existingId : !!business.newName.trim() && !!businessParentId;
      case 'farm':
        return (!fieldSeasonOnly && farm.mode === 'skip') || (farm.mode === 'existing' ? !!farm.existingId : !!farm.newName.trim());
      case 'field':
        return (!fieldSeasonOnly && field.mode === 'skip') || (field.mode === 'existing' ? !!field.existingId : !!field.newName.trim());
      case 'season':
        return (
          (!fieldSeasonOnly && timePeriod.mode === 'skip') ||
          (timePeriod.mode === 'existing'
            ? !!timePeriod.existingId
            : timePeriod.mode === 'new' && !!timePeriod.newName.trim() && !!timePeriodStart && !!timePeriodEnd)
        );
      case 'product':
        if (product.mode === 'skip') return fieldSeasonOnly;
        return product.mode === 'new' ? !!product.newName.trim() : !!product.existingId;
      case 'unit':
        return (unit.mode === 'existing' ? !!unit.existingId : !!unit.newName.trim()) && !!operationTypeId;
      default:
        return true;
    }
  }, [stepKey, fieldSeasonOnly, business, businessParentId, farm, field, timePeriod, timePeriodStart, timePeriodEnd, product, unit, operationTypeId]);

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = {
        business:
          business.mode === 'existing'
            ? { existingId: business.existingId }
            : { newName: business.newName.trim(), newParentBusinessId: businessParentId },
        farm:
          farm.mode === 'skip'
            ? null
            : farm.mode === 'existing'
              ? { existingId: farm.existingId }
              : { newName: farm.newName.trim() },
        field:
          field.mode === 'skip'
            ? null
            : field.mode === 'existing'
              ? { existingId: field.existingId }
              : { newName: field.newName.trim() },
        timePeriod:
          timePeriod.mode === 'skip'
            ? null
            : timePeriod.mode === 'existing'
              ? { existingId: timePeriod.existingId }
              : { newName: timePeriod.newName.trim(), newStartDate: timePeriodStart, newEndDate: timePeriodEnd },
        // A skipped variety (field-season rows only) sends neither id nor
        // name; its Gene Stack pick still applies.
        product:
          product.mode === 'group'
            ? { productGroupId: product.existingId }
            : {
                existingId: product.mode === 'existing' ? product.existingId : null,
                newName: product.mode === 'new' ? product.newName.trim() : null,
                category:
                  category.mode === 'skip' || product.mode === 'skip'
                    ? null
                    : category.mode === 'existing'
                      ? { existingId: category.existingId }
                      : { newName: category.newName.trim() },
                geneStack:
                  !isSeedRow || geneStack.mode === 'skip'
                    ? null
                    : geneStack.mode === 'existing'
                      ? { existingId: geneStack.existingId }
                      : { newName: geneStack.newName.trim() },
              },
        // Not sent for a field-season row, which has no rate or operation.
        ...(fieldSeasonOnly
          ? {}
          : {
              unit: unit.mode === 'existing' ? { existingId: unit.existingId } : { newName: unit.newName.trim() },
              operationTypeId,
            }),
        // Always sent — it's the Record's Region association (what
        // regional aggregation keys on), not just a location fallback.
        regionId,
        // Only when the admin actually set or changed it — an untouched
        // file date is already the row's own EventDate, and sending it
        // would also stamp it onto undated similar rows.
        recordDate: !fieldSeasonOnly && recordDate && recordDate !== fileDate ? recordDate : null,
        applyToSimilar,
        applyProductToSimilar,
        applyCategoryToSimilar,
        applyGeneStackToSimilar,
        applyBusinessToSimilar,
        applyFarmToSimilar,
        applyFieldToSimilar,
        pests: fieldSeasonOnly ? [] : Object.entries(pestFields).map(([rawText, f]) => ({
          rawText,
          existingId: f.mode === 'existing' ? f.existingId : null,
          newName: f.mode === 'new' ? f.newName.trim() : null,
          skip: f.mode === 'skip',
        })),
      };

      const response = await fetch(`/api/import-uploads/rows/${row.id}/match`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to save match');
      }
      onMatched();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setSaving(false);
    }
  };

  const renderModeField = (
    optionLabel: string,
    fieldState: FieldState,
    setFieldStateFn: (s: FieldState) => void,
    options: Option[],
    suggestion?: Option,
    allowSkip = false,
    newNameLabel = 'Name'
  ) => (
    <Box>
      <ToggleButtonGroup
        value={fieldState.mode}
        exclusive
        size="small"
        onChange={(_, value) => value && setFieldStateFn({ ...fieldState, mode: value })}
        sx={{ mb: 2 }}
      >
        <ToggleButton value="existing">Use Existing</ToggleButton>
        <ToggleButton value="new">Create New</ToggleButton>
        {allowSkip && <ToggleButton value="skip">None</ToggleButton>}
      </ToggleButtonGroup>

      {suggestion && fieldState.mode === 'existing' && fieldState.existingId === suggestion.id && (
        <Chip label={`Suggested match: ${suggestion.name}`} size="small" color="primary" variant="outlined" sx={{ mb: 2, display: 'block', width: 'fit-content' }} />
      )}

      {fieldState.mode === 'existing' && (
        <Autocomplete
          options={options}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          value={options.find((o) => o.id === fieldState.existingId) || null}
          onChange={(_, value) => setFieldStateFn({ ...fieldState, existingId: value?.id || null })}
          renderInput={(params) => <TextField {...params} label={`Existing ${optionLabel}`} />}
        />
      )}
      {fieldState.mode === 'new' && (
        <TextField
          fullWidth
          label={newNameLabel}
          value={fieldState.newName}
          onChange={(e) => setFieldStateFn({ ...fieldState, newName: e.target.value })}
        />
      )}
    </Box>
  );

  const renderRegionPicker = () => (
    <>
      <Typography variant="subtitle2" gutterBottom>Region</Typography>
      <Autocomplete
        options={regions}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        value={regions.find((o) => o.id === regionId) || null}
        onChange={(_, value) => setRegionId(value?.id || null)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Region (optional)"
            helperText={`The ${fieldSeasonOnly ? 'field season' : 'record'} is grouped under this region for regional reporting. Left blank, it uses the farm/field's associated region, if any.`}
          />
        )}
      />
    </>
  );

  const renderStepContent = () => {
    switch (stepKey) {
      case 'business':
        return (
          <Box>
            {renderModeField('Business', business, setBusiness, businesses, suggestions?.business)}
            {business.mode === 'new' && (
              <Autocomplete
                sx={{ mt: 2 }}
                options={businesses}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={businesses.find((o) => o.id === businessParentId) || null}
                onChange={(_, value) => setBusinessParentId(value?.id || null)}
                renderInput={(params) => <TextField {...params} label="Parent Business (required)" helperText="New businesses must be grouped under an existing one" />}
              />
            )}
          </Box>
        );
      case 'farm':
        return renderModeField('Farm', farm, setFarm, farms, suggestions?.farm, !fieldSeasonOnly, 'Farm Name');
      case 'field':
        return needsFarm ? (
          renderModeField('Field', field, setFieldState, fields, suggestions?.field, !fieldSeasonOnly, 'Field Name')
        ) : (
          <Alert severity="info">No Farm resolved — Field is skipped.</Alert>
        );
      case 'season':
        return (
          <Box>
            {renderModeField('Season', timePeriod, setTimePeriod, timePeriods, suggestions?.timePeriod, !fieldSeasonOnly, 'Season Name')}
            {timePeriod.mode === 'new' && (
              <Box display="flex" gap={2} mt={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={timePeriodStart}
                  onChange={(e) => setTimePeriodStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={timePeriodEnd}
                  onChange={(e) => setTimePeriodEnd(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            )}

            {!fieldSeasonOnly && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant="subtitle2" gutterBottom>Date of operation</Typography>
                <TextField
                  label="Date"
                  type="date"
                  value={recordDate}
                  onChange={(e) => setRecordDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 220 }}
                  helperText={recordDateHelperText}
                />
                {seasonRange && recordDate && (recordDate < seasonRange.start || recordDate > seasonRange.end) && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {formatDateInput(recordDate)} is outside the selected season ({formatDateInput(seasonRange.start)} – {formatDateInput(seasonRange.end)}).
                  </Alert>
                )}
              </>
            )}
          </Box>
        );
      case 'product':
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>{fieldSeasonOnly ? 'Variety' : 'Product'}</Typography>
            {fieldSeasonOnly && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                The Planting Seed product grown in this field this season. Optional — choose None if the file doesn't say.
              </Typography>
            )}
            <ToggleButtonGroup
              value={product.mode}
              exclusive
              size="small"
              onChange={(_, value) => value && setProduct({ ...product, mode: value })}
              sx={{ mb: 2 }}
            >
              <ToggleButton value="existing">Use Existing</ToggleButton>
              <ToggleButton value="new">Create New</ToggleButton>
              {fieldSeasonOnly ? <ToggleButton value="skip">None</ToggleButton> : <ToggleButton value="group">Use Product Group</ToggleButton>}
            </ToggleButtonGroup>
            {suggestions?.product && product.mode === 'existing' && product.existingId === suggestions.product.id && (
              <Chip label={`Suggested match: ${suggestions.product.name}`} size="small" color="primary" variant="outlined" sx={{ mb: 2, display: 'block', width: 'fit-content' }} />
            )}
            {product.mode === 'skip' ? null : product.mode === 'group' ? (
              <>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Use this when the import doesn't name a specific product precisely enough to match — the record still
                  carries the real Rate/Unit, just against a generic "roughly these active ingredients" bucket instead of
                  one exact product.
                </Alert>
                <Autocomplete
                  options={productGroups}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={productGroups.find((o) => o.id === product.existingId) || null}
                  onInputChange={(_, value) => setProductGroupSearch(value)}
                  onChange={(_, value) => setProduct({ ...product, existingId: value?.id || null })}
                  renderInput={(params) => <TextField {...params} label="Search product groups" />}
                />
              </>
            ) : product.mode === 'existing' ? (
              <>
                <Autocomplete
                  options={products}
                  // The `products` list is already the server's filtered
                  // result (name OR active ingredient match, see the
                  // productSearch effect above) — without this, MUI's
                  // default client-side filter re-applies on top of it using
                  // only getOptionLabel (the product name), silently hiding
                  // every result that matched by active ingredient instead.
                  filterOptions={(x) => x}
                  getOptionLabel={(o) => o.name}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={products.find((o) => o.id === product.existingId) || null}
                  onInputChange={(_, value) => setProductSearch(value)}
                  onChange={(_, value) => setProduct({ ...product, existingId: value?.id || null })}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        {option.activeIngredientDetails && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {option.activeIngredientDetails}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => <TextField {...params} label="Search products" />}
                />
                {(() => {
                  const selected = products.find((o) => o.id === product.existingId);
                  return selected?.activeIngredientDetails ? (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 1.5 }}>
                      {selected.activeIngredientDetails}
                    </Typography>
                  ) : null;
                })()}
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Product Name"
                  value={product.newName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProduct({ ...product, newName: value });
                    // Search on the part before the first "(" — these raw
                    // import names are typically "Brand (active ingredient
                    // detail)" (e.g. "Pegasus (Diafenthiuron 500 g/L SC)"),
                    // and searching the whole string would never match an
                    // existing product's much shorter name.
                    setProductSearch(value.split('(')[0].trim());
                  }}
                />
                {product.newName.trim().length >= 3 && products.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {products.length === 1 ? 'A similar product already exists' : 'Similar products already exist'} — use one instead to avoid creating a duplicate:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {products.slice(0, 5).map((p) => (
                        <Chip
                          key={p.id}
                          label={p.activeIngredientDetails ? `${p.name} (${p.activeIngredientDetails})` : p.name}
                          size="small"
                          clickable
                          color="warning"
                          variant="outlined"
                          onClick={() => setProduct({ mode: 'existing', existingId: p.id, newName: '' })}
                        />
                      ))}
                    </Box>
                  </Alert>
                )}
              </>
            )}

            {product.mode !== 'group' && product.mode !== 'skip' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Category</Typography>
                {renderModeField('Category', category, setCategory, productCategories, suggestions?.productCategory, true, 'Category Name')}
              </>
            )}

            {product.mode !== 'group' && isSeedRow && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>Gene Stack</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  This row's gene stack. For a Planting Seed product it also becomes the variety's own, unless the variety already has one — which then wins.
                </Typography>
                {renderModeField('Gene Stack', geneStack, setGeneStack, geneStacks, suggestions?.geneStack, true, 'Gene Stack Name')}
              </>
            )}
          </Box>
        );
      case 'pests':
        return suggestions && suggestions.pests.length > 0 ? (
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Each pest/weed name found on this row resolves to a Species, same as Product — pick an existing one, create a new one, or skip it.
            </Typography>
            {suggestions.pests.map((p) => (
              <Box key={p.rawText} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>{p.rawText}</Typography>
                {renderModeField(
                  'Species',
                  pestFields[p.rawText] || emptyField(),
                  (s) => setPestFields((prev) => ({ ...prev, [p.rawText]: s })),
                  species,
                  p.species,
                  true,
                  'Species Name'
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Alert severity="info">No pests recorded for this row.</Alert>
        );
      case 'unit':
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>Rate Unit</Typography>
            {renderModeField('Unit', unit, setUnit, units, suggestions?.unit, false, 'Unit Name (e.g. Kg/Ha)')}

            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>Operation Type</Typography>
            <Autocomplete
              options={operationTypes}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={operationTypes.find((o) => o.id === operationTypeId) || null}
              onChange={(_, value) => setOperationTypeId(value?.id || null)}
              renderInput={(params) => <TextField {...params} label="Operation Type" />}
            />
            {suggestions?.operationType && operationTypeId === suggestions.operationType.id && (
              <Chip label={`Suggested: ${suggestions.operationType.name}`} size="small" color="primary" variant="outlined" sx={{ mt: 1 }} />
            )}

            <Divider sx={{ my: 2 }} />
            {renderRegionPicker()}
          </Box>
        );
      case 'review': {
        const businessLabel = business.mode === 'existing' ? businesses.find((b) => b.id === business.existingId)?.name : `${business.newName} (new)`;
        const farmLabel = farm.mode === 'skip' ? 'None' : farm.mode === 'existing' ? farms.find((f) => f.id === farm.existingId)?.name : `${farm.newName} (new)`;
        const fieldLabel = field.mode === 'skip' ? 'None' : field.mode === 'existing' ? fields.find((f) => f.id === field.existingId)?.name : `${field.newName} (new)`;
        const seasonLabel = timePeriod.mode === 'skip' ? 'None' : timePeriod.mode === 'existing' ? timePeriods.find((t) => t.id === timePeriod.existingId)?.name : `${timePeriod.newName} (new)`;
        const selectedProduct = product.mode === 'existing' ? products.find((p) => p.id === product.existingId) : undefined;
        const productLabel =
          product.mode === 'skip'
            ? 'None'
            : product.mode === 'group'
            ? `${productGroups.find((g) => g.id === product.existingId)?.name || 'Product group'} (generic)`
            : product.mode === 'existing'
              ? selectedProduct?.name || suggestions?.product?.name
              : `${product.newName} (new)`;
        const categoryLabel = category.mode === 'skip' ? 'None' : category.mode === 'existing' ? productCategories.find((c) => c.id === category.existingId)?.name : `${category.newName} (new)`;
        const geneStackLabel = !isSeedRow || geneStack.mode === 'skip' ? 'None' : geneStack.mode === 'existing' ? geneStacks.find((g) => g.id === geneStack.existingId)?.name : `${geneStack.newName} (new)`;
        const unitLabel = unit.mode === 'existing' ? units.find((u2) => u2.id === unit.existingId)?.name : `${unit.newName} (new)`;
        const operationLabel = operationTypes.find((o) => o.id === operationTypeId)?.name;
        const regionLabel = regionId ? regions.find((r) => r.id === regionId)?.name : 'None';
        // Same Field-else-Farm anchor ResolveMatchAsync uses — a brand-new
        // field/farm has no boundary yet, so no mapped area either.
        const anchorFeature =
          field.mode !== 'skip'
            ? field.mode === 'existing' ? fields.find((f) => f.id === field.existingId) : undefined
            : farm.mode === 'existing' ? farms.find((f) => f.id === farm.existingId) : undefined;
        const mappedArea = anchorFeature?.areaHectares ?? null;
        const bandSuffix = isBanded(row.bandPercent) ? ` at ${row.bandPercent}% band` : '';
        const pestLabels = (suggestions?.pests || [])
          .map((p) => {
            const f = pestFields[p.rawText];
            if (!f || f.mode === 'skip') return null;
            return f.mode === 'existing' ? species.find((s) => s.id === f.existingId)?.name : `${f.newName} (new)`;
          })
          .filter((l): l is string => !!l);

        const fieldSeasonValues = suggestions?.fieldSeasonValues || [];

        return (
          <Box>
            {fieldSeasonOnly ? (
              <>
                {renderRegionPicker()}
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2">Business: <strong>{businessLabel}</strong></Typography>
                <Typography variant="body2">Farm: <strong>{farmLabel}</strong></Typography>
                <Typography variant="body2">Field: <strong>{fieldLabel}</strong></Typography>
                <Typography variant="body2">Season: <strong>{seasonLabel}</strong></Typography>
                <Typography variant="body2">Variety: <strong>{productLabel}</strong></Typography>
                {product.mode !== 'skip' && <Typography variant="body2">Category: <strong>{categoryLabel}</strong></Typography>}
                <Typography variant="body2">Gene Stack: <strong>{geneStackLabel}</strong></Typography>
                <Typography variant="body2">Area: <strong>{row.areaHectares != null ? `${row.areaHectares} ha (from file)` : 'None in file'}</strong></Typography>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Field season values</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Fills this field's season. A value here replaces what an earlier import stored; a blank cell leaves it alone.
                </Typography>
                {fieldSeasonValues.length > 0 ? (
                  <FieldSeasonValuesTable values={fieldSeasonValues} />
                ) : (
                  <Alert severity="info">No field season values in this row.</Alert>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2">
                  Record Date:{' '}
                  <strong>
                    {recordDate
                      ? `${formatDateInput(recordDate)}${recordDate === fileDate ? ' (from file)' : ''}`
                      : fileDate
                        ? `${formatDateInput(fileDate)} (from file)`
                        : `None set — will use ${fallbackDateText}`}
                  </strong>
                </Typography>
                <Typography variant="body2">Business: <strong>{businessLabel}</strong></Typography>
                <Typography variant="body2">Farm: <strong>{farmLabel}</strong></Typography>
                <Typography variant="body2">Field: <strong>{fieldLabel}</strong></Typography>
                <Typography variant="body2">Season: <strong>{seasonLabel}</strong></Typography>
                <Typography variant="body2">
                  Product: <strong>{productLabel}</strong>
                  {selectedProduct?.activeIngredientDetails && ` (${selectedProduct.activeIngredientDetails})`}
                </Typography>
                {product.mode !== 'group' && <Typography variant="body2">Category: <strong>{categoryLabel}</strong></Typography>}
                {product.mode !== 'group' && isSeedRow && <Typography variant="body2">Gene Stack: <strong>{geneStackLabel}</strong></Typography>}
                <Typography variant="body2">
                  Rate: <strong>{row.rate ?? '-'} {unitLabel}</strong>
                  {row.rate != null && isBanded(row.bandPercent) && `${bandSuffix} = ${formatRate(effectiveRate(row.rate, row.bandPercent))} effective`}
                </Typography>
                <Typography variant="body2">
                  Area: <strong>{row.areaHectares != null ? `${row.areaHectares} ha${bandSuffix} (from file)` : 'None in file — will use the mapped area'}</strong>
                  {' · '}Mapped: <strong>{mappedArea != null ? `${mappedArea} ha` : 'no boundary drawn yet'}</strong>
                </Typography>
                <Typography variant="body2">Operation Type: <strong>{operationLabel}</strong></Typography>
                {pestLabels.length > 0 && <Typography variant="body2">Pests: <strong>{pestLabels.join(', ')}</strong></Typography>}
                <Typography variant="body2">Region: <strong>{regionLabel}</strong></Typography>
                {fieldSeasonValues.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 2 }}>Also fills this field's season</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {field.mode !== 'skip' && timePeriod.mode !== 'skip'
                        ? 'These paddock columns are stored on the field season, not the record.'
                        : 'Needs a Field and Season — without them these stay on the record as extra columns.'}
                    </Typography>
                    <FieldSeasonValuesTable values={fieldSeasonValues} />
                  </>
                )}
              </>
            )}

            {suggestions && suggestions.similarUnmatchedCount > 0 && (
              <FormControlLabel
                sx={{ mt: 2, display: 'block' }}
                control={<Checkbox checked={applyToSimilar} onChange={(e) => setApplyToSimilar(e.target.checked)} />}
                label={`Also apply this same match to ${suggestions.similarUnmatchedCount} other unmatched row(s) in this upload with the exact same Business/Farm/Field/Product${
                  recordDate && recordDate !== fileDate ? ' (the date too, for rows with no date of their own)' : ''
                }`}
              />
            )}

            {/* Pre-fills (not auto-commits) — an unmatched row elsewhere in
                the batch sharing this raw text will show this same match as
                its default next time it's reviewed, but still needs its own
                Business/Farm/etc confirmed individually. */}
            {suggestions && suggestions.similarUnmatchedBusinessCount > 0 && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={<Checkbox checked={applyBusinessToSimilar} onChange={(e) => setApplyBusinessToSimilar(e.target.checked)} />}
                label={`Also pre-fill this Business for ${suggestions.similarUnmatchedBusinessCount} other unmatched row(s) with the same raw business text`}
              />
            )}
            {farm.mode !== 'skip' && suggestions && suggestions.similarUnmatchedFarmCount > 0 && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={<Checkbox checked={applyFarmToSimilar} onChange={(e) => setApplyFarmToSimilar(e.target.checked)} />}
                label={`Also pre-fill this Farm for ${suggestions.similarUnmatchedFarmCount} other unmatched row(s) with the same raw farm text`}
              />
            )}
            {field.mode !== 'skip' && suggestions && suggestions.similarUnmatchedFieldCount > 0 && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={<Checkbox checked={applyFieldToSimilar} onChange={(e) => setApplyFieldToSimilar(e.target.checked)} />}
                label={`Also pre-fill this Field for ${suggestions.similarUnmatchedFieldCount} other unmatched row(s) with the same raw field text`}
              />
            )}
            {product.mode !== 'skip' && suggestions && suggestions.similarUnmatchedProductCount > 0 && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={<Checkbox checked={applyProductToSimilar} onChange={(e) => setApplyProductToSimilar(e.target.checked)} />}
                label={`Also pre-fill this Product for ${suggestions.similarUnmatchedProductCount} other unmatched row(s) with the same raw product text (they'll still need individual review)`}
              />
            )}
            {product.mode !== 'group' && category.mode !== 'skip' && suggestions && suggestions.similarUnmatchedCategoryCount > 0 && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={<Checkbox checked={applyCategoryToSimilar} onChange={(e) => setApplyCategoryToSimilar(e.target.checked)} />}
                label={`Also pre-fill this Category for ${suggestions.similarUnmatchedCategoryCount} other unmatched row(s) with the same raw category text`}
              />
            )}
            {product.mode !== 'group' && isSeedRow && geneStack.mode !== 'skip' && suggestions && suggestions.similarUnmatchedGeneStackCount > 0 && (
              <FormControlLabel
                sx={{ mt: 1, display: 'block' }}
                control={<Checkbox checked={applyGeneStackToSimilar} onChange={(e) => setApplyGeneStackToSimilar(e.target.checked)} />}
                label={`Also pre-fill this Gene Stack for ${suggestions.similarUnmatchedGeneStackCount} other unmatched row(s) with the same raw gene stack text`}
              />
            )}
          </Box>
        );
      }
      default:
        return null;
    }
  };

  return (
    // One height for every step, so Next stays in the same place while
    // clicking through; a long step scrolls instead of growing the dialog.
    // The stepper stays put above the scrolling part.
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: 'min(860px, calc(100% - 64px))' } }}>
      <DialogTitle>
        Match Imported Row
        {row.eventDate && (
          <Typography variant="body2" color="text.secondary">
            Date: {new Date(row.eventDate).toLocaleDateString()}
          </Typography>
        )}
      </DialogTitle>
      {!loading && !suggestions?.alreadyMatched && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((key) => (
              <Step key={key}>
                <StepLabel>{stepLabels(fieldSeasonOnly)[key]}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : suggestions?.alreadyMatched ? (
          <Alert severity="info">
            {suggestions.existingRecordId
              ? `This row is already matched to Record ${suggestions.existingRecordId}.`
              : 'This row has already filled its field season.'}
          </Alert>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {renderStepContent()}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!loading && !suggestions?.alreadyMatched && (
          <>
            {activeStep > 0 && <Button onClick={() => setActiveStep((s) => s - 1)}>Back</Button>}
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" disabled={!canAdvance} onClick={() => setActiveStep((s) => s + 1)}>
                Next
              </Button>
            ) : (
              <Button variant="contained" disabled={saving} onClick={handleConfirm}>
                {saving ? 'Saving...' : 'Confirm Match'}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportedRowMatchWizard;
